/**
 * React Query Hooks for Contract Groups
 *
 * Provides data fetching and caching for contract group operations using TanStack Query.
 */
import { useQuery, useMutation, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { contractGroupService, ContractGroup, CreateContractGroupData, UpdateContractGroupData } from '../../services/api';
import { queryKeys, STALE_TIMES } from '../../lib/queryClient';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Hook to fetch all contract groups
 * @param options - Additional query options
 */
export function useContractGroups(
  options?: Omit<UseQueryOptions<ContractGroup[], Error>, 'queryKey' | 'queryFn'>
) {
  const { token } = useAuth();

  return useQuery({
    queryKey: queryKeys.contractGroups.list(),
    queryFn: async () => {
      if (!token) throw new Error('No authentication token');
      return contractGroupService.getAll(token);
    },
    enabled: !!token,
    staleTime: STALE_TIMES.STATIC,
    ...options,
  });
}

/**
 * Hook to fetch only active contract groups
 * @param options - Additional query options
 */
export function useActiveContractGroups(
  options?: Omit<UseQueryOptions<ContractGroup[], Error>, 'queryKey' | 'queryFn'>
) {
  const { token } = useAuth();

  return useQuery({
    queryKey: [...queryKeys.contractGroups.list(), 'active'],
    queryFn: async () => {
      if (!token) throw new Error('No authentication token');
      return contractGroupService.getActive(token);
    },
    enabled: !!token,
    staleTime: STALE_TIMES.STATIC,
    ...options,
  });
}

/**
 * Hook to fetch a single contract group by ID
 * @param groupId - The contract group ID to fetch
 * @param options - Additional query options
 */
export function useContractGroup(
  groupId: string | undefined,
  options?: Omit<UseQueryOptions<ContractGroup | null, Error>, 'queryKey' | 'queryFn'>
) {
  const { token } = useAuth();

  return useQuery({
    queryKey: queryKeys.contractGroups.detail(groupId || ''),
    queryFn: async () => {
      if (!token) throw new Error('No authentication token');
      if (!groupId) throw new Error('Contract Group ID required');
      return contractGroupService.getOne(token, groupId);
    },
    enabled: !!token && !!groupId,
    staleTime: STALE_TIMES.STATIC,
    ...options,
  });
}

/**
 * Hook to create a new contract group
 */
export function useCreateContractGroup() {
  const { token } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (groupData: CreateContractGroupData) => {
      if (!token) throw new Error('No authentication token');
      return contractGroupService.create(token, groupData);
    },
    onSuccess: () => {
      // Invalidate contract group list queries to refetch
      queryClient.invalidateQueries({ queryKey: queryKeys.contractGroups.list() });
    },
  });
}

/**
 * Hook to update an existing contract group
 */
export function useUpdateContractGroup() {
  const { token } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: UpdateContractGroupData }) => {
      if (!token) throw new Error('No authentication token');
      return contractGroupService.update(token, id, updates);
    },
    onSuccess: (_, { id }) => {
      // Invalidate both detail and list queries
      queryClient.invalidateQueries({ queryKey: queryKeys.contractGroups.detail(id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.contractGroups.list() });
    },
  });
}

/**
 * Hook to delete a contract group
 */
export function useDeleteContractGroup() {
  const { token } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (groupId: string) => {
      if (!token) throw new Error('No authentication token');
      return contractGroupService.delete(token, groupId);
    },
    onSuccess: (_, groupId) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: queryKeys.contractGroups.detail(groupId) });
      // Invalidate list queries
      queryClient.invalidateQueries({ queryKey: queryKeys.contractGroups.list() });
    },
  });
}
